import { ChangeDetectionStrategy, Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { finalize, takeUntil } from 'rxjs/operators';
import { LegacyIncidentDownloadService } from 'src/app/_services/reports/legacy-incident-download.service';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ChangeDetectorRef } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { ConfirmDialogComponent } from 'src/app/_components/shared/confirm-dialog/confirm-dialog.component';

type DownloadKey = 'analysis' | 'decisions' | 'incidentInfo' | 'otherIncidentInfo';

@Component({
  selector: 'wfdss-legacy-incident-download-dialog',
  standalone: false,
  templateUrl: './legacy-incident-download-dialog.component.html',
  styleUrl: './legacy-incident-download-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LegacyIncidentDownloadDialogComponent implements OnDestroy {

  private _destroying = new Subject<void>()
  private activeSubscription: Subscription | null = null;

  errorMessage: string | null = null;
  largeFileWarning: string | null = null;

  progress = 0;
  totalSize = 0;
  
  activeDownloadKey: DownloadKey | null = null;

  downloading: Record<DownloadKey, boolean> = {
    analysis: false,
    decisions: false,
    incidentInfo: false,
    otherIncidentInfo: false
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { wfdssId: number; incidentName: string; uniqueFireIdentifier: string },
    private dialogRef: MatDialogRef<LegacyIncidentDownloadDialogComponent>,
    private downloadService: LegacyIncidentDownloadService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog) { }

  ngOnDestroy(): void {
    this.cancelDownload();
    this._destroying.next();
    this._destroying.complete();
  }

  get isAnyDownloading(): boolean {
    return this.activeDownloadKey !== null;
  }

  close() {
    if (!this.isAnyDownloading) {
      this.dialogRef.close();
      return;
    }

    const confirmRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        message: 'A download is currently in progress. Do you want to cancel the download and close the dialog?',
        okLabel: 'Yes, Cancel Download',
        cancelLabel: 'No, Continue Download'
      }
    });

    confirmRef.afterClosed().subscribe(result => {
      if (result == true) {
        this.cancelDownload();
        this.dialogRef.close();
      }

    });
  }

  private showLargeFileWarning(totalBytes: number) {
    const MB = 1024 * 1024;
    const threshold = 300 * MB;

    if (!this.largeFileWarning && totalBytes && totalBytes >= threshold) {
      const sizeMB = (totalBytes / MB).toFixed(0);
      this.largeFileWarning = `This file is large (${sizeMB} MB) and may take some time to download.
      Do not close this dialog until the download is complete.`;
    }
  }

  private cancelDownload() {
    if (this.activeSubscription) {
      this.activeSubscription.unsubscribe();
      this.activeSubscription = null;
    }

    if (this.activeDownloadKey) {
      this.downloading[this.activeDownloadKey] = false;
    }

    this.activeDownloadKey = null;
    this.progress = 0;
    this.largeFileWarning = null;

    this.cdr.markForCheck();
  }

  download(key: DownloadKey) {

    if (this.isAnyDownloading) {
      this.largeFileWarning = 'Another download is already in progress. Please wait for the current download to complete before starting a new one.';
      this.cdr.markForCheck();
      return;
    }

    this.errorMessage = null;
    this.largeFileWarning = null;
    this.progress = 0;
    this.totalSize = 0;

    this.activeDownloadKey = key;
    this.downloading[key] = true;
    
    this.cdr.markForCheck();

    this.downloadService.downloadFile(key, this.data.wfdssId, this.data.incidentName, this.data.uniqueFireIdentifier)
    .pipe(
      takeUntil(this._destroying.asObservable()),
      finalize(() => {
        this.downloading[key] = false;
        this.activeDownloadKey = null;
        this.progress = 0;
        this.cdr.markForCheck();
      })
    )
    .subscribe({
      next: (data) => {
        this.progress = data.progress;

    if (data.totalSize && data.totalSize > 0) {
          this.totalSize = data.totalSize;
          this.showLargeFileWarning(this.totalSize);
        }
        this.cdr.markForCheck();
        },
      error: (error: Error) => {
          this.errorMessage = error.message;
          this.cdr.markForCheck();
      },
      complete: () => {}
    });
  }

}
